/**
 * The inline `<span>` a markdown body carries per-run size and colour as.
 *
 * Markdown has no syntax for either, so a styled run is stored as a literal
 * `<span size="11px" color="#c00000">` tag, mirroring the fp-* container
 * attribute vocabulary. Only `size` and `color` make a span a styling run, and
 * only with a value that validates here - any other attribute, an unparsable
 * value, or a duplicate leaves the tag as literal text rather than letting an
 * arbitrary CSS value reach the rendered style.
 */

/** The per-run appearance a styled `<span>` carries; both parts optional. */
export interface InlineTextSpanAttributes {
  size: string | null;
  color: string | null;
}

/** A change to a run's appearance: null clears the part, absent leaves it. */
export interface InlineTextSpanPatch {
  size?: string | null;
  color?: string | null;
}

/** Data attribute carrying a rendered span's authored size back to the parser. */
export const INLINE_TEXT_SPAN_SIZE_PROPERTY = "data-fp-size";

/** Data attribute carrying a rendered span's authored colour back to the parser. */
export const INLINE_TEXT_SPAN_COLOR_PROPERTY = "data-fp-color";

/** The closing tag of a styled span run. */
export const INLINE_TEXT_SPAN_CLOSE_TAG = "</span>";

/** A whole-pixel or one-decimal pixel length, the only size form accepted. */
const SIZE_PATTERN = /^\d+(?:\.\d+)?px$/;

/** The colour forms accepted: 3-, 6- and 8-digit hex, and rgb()/rgba(). */
const COLOR_PATTERNS = [
  /^#[0-9a-f]{3}$/i,
  /^#[0-9a-f]{6}$/i,
  /^#[0-9a-f]{8}$/i,
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\)$/i,
];

/** A span open tag carrying at least one double- or single-quoted attribute. */
const SPAN_OPEN_TAG = /^<span((?:\s+[a-z-]+\s*=\s*(?:"[^"]*"|'[^']*'))+)\s*>$/i;

/** One `name="value"` pair inside a span open tag. */
const SPAN_ATTRIBUTE = /([a-z-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;

/**
 * Whether a value is an accepted run size.
 * @param value - The candidate size
 * @returns True when the value is a pixel length
 */
export function isInlineTextSpanSize(value: string): boolean {
  return SIZE_PATTERN.test(value.trim());
}

/**
 * Whether a value is an accepted run colour.
 * @param value - The candidate colour
 * @returns True when the value is a hex or rgb()/rgba() colour
 */
export function isInlineTextSpanColor(value: string): boolean {
  const trimmed = value.trim();
  return COLOR_PATTERNS.some((pattern) => pattern.test(trimmed));
}

/**
 * Reads a span open tag as a styling run.
 * @param raw - The raw tag text, e.g. `<span size="11px">`
 * @returns The run's appearance, or null when the tag is not a styling run
 */
export function parseInlineTextSpanTag(
  raw: string,
): InlineTextSpanAttributes | null {
  const match = SPAN_OPEN_TAG.exec(raw.trim());
  if (!match) {
    return null;
  }
  const pairs = Array.from(match[1].matchAll(SPAN_ATTRIBUTE)).map((entry) => ({
    name: entry[1].toLowerCase(),
    value: entry[2] ?? entry[3] ?? "",
  }));
  const size = pairs.find((pair) => pair.name === "size");
  const color = pairs.find((pair) => pair.name === "color");
  const named = (size ? 1 : 0) + (color ? 1 : 0);
  if (named === 0 || named !== pairs.length) {
    return null;
  }
  if (size && !isInlineTextSpanSize(size.value)) {
    return null;
  }
  if (color && !isInlineTextSpanColor(color.value)) {
    return null;
  }
  return { size: size?.value ?? null, color: color?.value ?? null };
}

/**
 * Narrows an untrusted attribute bag (editor JSON, DOM attributes) to a
 * styling run, dropping each part that doesn't validate.
 * @param value - The raw attribute bag
 * @returns The run's appearance, or null when neither part survives
 */
export function readInlineTextSpanAttributes(
  value: unknown,
): InlineTextSpanAttributes | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const record: Record<string, unknown> = Object.fromEntries(
    Object.entries(value),
  );
  return buildAttributes(record.size, record.color);
}

/**
 * Applies a patch to a run's appearance, dropping the run entirely when
 * nothing is left to style.
 * @param current - The run's current appearance, or null when unstyled
 * @param patch - The parts to set (a value) or clear (null)
 * @returns The patched appearance, or null when both parts end up unset
 */
export function mergeInlineTextSpanAttributes(
  current: InlineTextSpanAttributes | null,
  patch: InlineTextSpanPatch,
): InlineTextSpanAttributes | null {
  return buildAttributes(
    patch.size === undefined ? current?.size : patch.size,
    patch.color === undefined ? current?.color : patch.color,
  );
}

/**
 * The stored open tag for a styling run, attributes in canonical order.
 * @param attributes - The run's appearance
 * @returns The `<span ...>` open tag
 */
export function inlineTextSpanTag(
  attributes: InlineTextSpanAttributes,
): string {
  const size = attributes.size === null ? "" : ` size="${attributes.size}"`;
  const color = attributes.color === null ? "" : ` color="${attributes.color}"`;
  return `<span${size}${color}>`;
}

/**
 * The inline style declaring a styling run's appearance.
 * @param attributes - The run's appearance
 * @returns The style attribute text
 */
export function inlineTextSpanStyleText(
  attributes: InlineTextSpanAttributes,
): string {
  return [
    attributes.size === null ? null : `font-size:${attributes.size}`,
    attributes.color === null ? null : `color:${attributes.color}`,
  ]
    .flatMap((declaration) => (declaration === null ? [] : [declaration]))
    .join(";");
}

/**
 * The hast properties a rendered styling run carries: the style it draws with,
 * plus the authored values the edit session reads back off the rendered DOM.
 * @param attributes - The run's appearance
 * @returns The properties for the rendered `<span>`
 */
export function inlineTextSpanProperties(
  attributes: InlineTextSpanAttributes,
): Record<string, string> {
  return {
    style: inlineTextSpanStyleText(attributes),
    ...(attributes.size === null
      ? {}
      : { [INLINE_TEXT_SPAN_SIZE_PROPERTY]: attributes.size }),
    ...(attributes.color === null
      ? {}
      : { [INLINE_TEXT_SPAN_COLOR_PROPERTY]: attributes.color }),
  };
}

/**
 * Builds a validated appearance from two untrusted values.
 * @param size - The candidate size
 * @param color - The candidate colour
 * @returns The appearance, or null when neither part validates
 */
function buildAttributes(
  size: unknown,
  color: unknown,
): InlineTextSpanAttributes | null {
  const validSize =
    typeof size === "string" && isInlineTextSpanSize(size) ? size.trim() : null;
  const validColor =
    typeof color === "string" && isInlineTextSpanColor(color)
      ? color.trim()
      : null;
  if (validSize === null && validColor === null) {
    return null;
  }
  return { size: validSize, color: validColor };
}
